import { InitialSelection } from 'components/discover/publications/video-browser/hooks/use-default-selection';
import { CategoryNode, CategoryNodes, KeywordNode } from 'components/discover/publications/video-browser/model';
import { first } from 'lodash-es';
import { useMemo, useState } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';

export function useSelection(tree: CategoryNodes, defaultSelection: InitialSelection) {
  const [activeCategoryId, setActiveCategoryId] = useState<number>(defaultSelection.categoryId);
  const [activeKeywordId, setActiveKeywordId] = useState<number | null>(defaultSelection.keywordId);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(defaultSelection.videoId);

  const selectCategory = (category: CategoryNode) => {
    setActiveCategoryId(category.id);
    setActiveKeywordId(null);
    logAnalytics.trackEvent('Discover', 'See more videos - Select category', category.name);
    setActiveVideoId(null);
  };

  const selectCategoryAndKeyword = (category: CategoryNode, keyword: KeywordNode | null) => {
    setActiveCategoryId(category.id);
    setActiveKeywordId(keyword?.id ?? null);
    logAnalytics.trackEvent(
      'Discover',
      keyword !== null ? 'See more videos - Select keyword' : 'See more videos - Select category',
      keyword !== null ? `${category.name} / ${keyword.name}` : category.name,
    );
    setActiveVideoId(null);
  };

  const data = useMemo(() => {
    const activeCategoryNode = tree.find(c => c.id === activeCategoryId);
    const activeKeywordNode = activeCategoryNode?.children.find(k => k.id === activeKeywordId);
    const activeLabels = [activeCategoryNode?.name, activeKeywordNode?.name].filter(x => x !== undefined);
    const videoList = activeKeywordNode?.items ?? activeCategoryNode?.items ?? [];
    const defaultVideo = first(videoList)!;
    const activeVideoItem = videoList.find(v => v.videoId === activeVideoId) ?? defaultVideo;
    return {
      activeLabels,
      activeVideoItem,
      videoList,
      activeCategory: activeCategoryNode ?? null,
      activeKeyword: activeKeywordNode ?? null,
    };
  }, [activeKeywordId, activeVideoId, activeCategoryId, tree]);

  return { ...data, selectCategory, selectCategoryAndKeyword, setActiveVideoId };
}
